/**
 * The ranked list's metric columns, read off the formula that was scored:
 * one column per (range, metric) it reads — sliders' metrics, or every
 * metric named in its blocks (sub-formulas and master) — labelled the Flow
 * Optimizer's way, with the range when the formula has several. At most
 * four; the rest live in a row's detail.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  collectFormulaIdentifiers,
  type DropGenHourFormula,
  type DropGenHourFormulaGroup,
  FLOW_FORMULA_METRICS,
  FLOW_METRIC_LABELS,
  hourRangeKey,
  hourRangeLabel,
  resolveEffectiveExpression,
} from '@trafficpulse/shared';

import { formatMetric } from '../../../general/hour/formula/hour-metric-catalog';

export interface RankingColumn {
  key: string;
  rangeKey: string;
  metric: string;
  label: string;
  /** The range, when the formula reads several. */
  rangeLabel: string | null;
  category: string;
}

export const MAX_COLUMNS = 4;

const CATEGORY_OF = new Map(FLOW_FORMULA_METRICS.map((m) => [m.id, m.category]));

function identifiers(expression: string): string[] {
  try {
    return [...collectFormulaIdentifiers(expression)];
  } catch {
    return [];
  }
}

/** The catalog metrics one range's formula reads, in the order it names them. */
export function metricsRead(group: DropGenHourFormulaGroup): string[] {
  const expressions =
    group.mode === 'blocks'
      ? [...(group.blocks?.subformulas ?? []).map((s) => s.expression ?? ''), resolveEffectiveExpression(group)]
      : (group.sliders?.metrics ?? []).map((m) => m.metric);
  const ids = group.mode === 'blocks' ? expressions.flatMap(identifiers) : expressions;
  return [...new Set(ids.filter((id) => id && CATEGORY_OF.has(id)))];
}

export function rankingColumns(formula: DropGenHourFormula): { columns: RankingColumn[]; hidden: number } {
  const several = new Set(formula.groups.map(hourRangeKey)).size > 1;
  const all = new Map<string, RankingColumn>();
  for (const g of formula.groups) {
    const rangeKey = hourRangeKey(g);
    for (const metric of metricsRead(g)) {
      const key = `${rangeKey}|${metric}`;
      if (all.has(key)) continue;
      all.set(key, {
        key,
        rangeKey,
        metric,
        label: FLOW_METRIC_LABELS[metric] ?? metric,
        rangeLabel: several ? hourRangeLabel(rangeKey) : null,
        category: CATEGORY_OF.get(metric) ?? 'advanced',
      });
    }
  }
  const list = [...all.values()];
  return { columns: list.slice(0, MAX_COLUMNS), hidden: Math.max(0, list.length - MAX_COLUMNS) };
}

/** The column's value for one row, or null when its range had no row. */
export const columnValue = (col: RankingColumn, metricsByRange: Record<string, Record<string, number>>): number | null => {
  const v = metricsByRange[col.rangeKey]?.[col.metric];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

export const columnText = (col: RankingColumn, v: number | null): string => (v === null ? '—' : formatMetric(col.metric, v));
